<template>
  <div class="filter-panel">
    <div class="filter-panel__content">
      <div class="text-caption grey--text mb-4">
        来店回数・売上・RFMセグメントは直近3ヶ月のデータで集計されます
      </div>

      <!-- RFMセグメントフィルター -->
      <div class="mb-4">
        <div class="text-subtitle-2 mb-2">
          RFMセグメント
        </div>
        <v-chip-group
          v-model="filters.rfmSegment"
          column
          multiple
        >
          <v-tooltip
            v-for="(label, key) in SEGMENT_LABELS"
            :key="key"
            bottom
          >
            <template #activator="{ on, attrs }">
              <v-chip
                :value="key"
                :color="SEGMENT_COLORS[key]"
                filter
                outlined
                v-bind="attrs"
                v-on="on"
              >
                {{ label }}
              </v-chip>
            </template>
            <span>{{ SEGMENT_DESCRIPTIONS[key] }}</span>
          </v-tooltip>
        </v-chip-group>
      </div>

      <!-- 来店回数フィルター -->
      <div class="mb-4">
        <v-text-field
          v-model.number="filters.minVisitCount"
          label="来店回数"
          type="number"
          suffix="回以上"
          min="0"
          dense
          outlined
          hide-details
        />
        <div
          v-if="filters.minVisitCount"
          class="mt-2"
        >
          <div class="text-caption grey--text mb-1">
            集計期間（未指定で直近3ヶ月）
          </div>
          <v-row dense>
            <v-col cols="6">
              <v-menu
                v-model="visitCountStartDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    v-model="filters.visitCountStartDate"
                    label="開始日"
                    :prepend-inner-icon="icons.mdiCalendar"
                    readonly
                    dense
                    outlined
                    hide-details
                    clearable
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="filters.visitCountStartDate = null"
                  />
                </template>
                <v-date-picker
                  v-model="filters.visitCountStartDate"
                  no-title
                  locale="ja"
                  :day-format="date => new Date(date).getDate()"
                  @input="visitCountStartDateMenu = false"
                />
              </v-menu>
            </v-col>
            <v-col cols="6">
              <v-menu
                v-model="visitCountEndDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    v-model="filters.visitCountEndDate"
                    label="終了日"
                    :prepend-inner-icon="icons.mdiCalendar"
                    readonly
                    dense
                    outlined
                    hide-details
                    clearable
                    v-bind="attrs"
                    v-on="on"
                    @click:clear="filters.visitCountEndDate = null"
                  />
                </template>
                <v-date-picker
                  v-model="filters.visitCountEndDate"
                  no-title
                  locale="ja"
                  :day-format="date => new Date(date).getDate()"
                  @input="visitCountEndDateMenu = false"
                />
              </v-menu>
            </v-col>
          </v-row>
        </div>
      </div>

      <!-- 最終来店日フィルター -->
      <div class="mb-4">
        <div class="text-subtitle-2 mb-2">
          最終来店日
        </div>
        <v-row dense>
          <v-col cols="6">
            <v-menu
              v-model="startDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.lastVisitAfter"
                  label="開始日"
                  :prepend-inner-icon="icons.mdiCalendar"
                  readonly
                  dense
                  outlined
                  hide-details
                  clearable
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="filters.lastVisitAfter = null"
                />
              </template>
              <v-date-picker
                v-model="filters.lastVisitAfter"
                no-title
                locale="ja"
                :day-format="date => new Date(date).getDate()"
                @input="startDateMenu = false"
              />
            </v-menu>
          </v-col>
          <v-col cols="6">
            <v-menu
              v-model="endDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="filters.lastVisitBefore"
                  label="終了日"
                  :prepend-inner-icon="icons.mdiCalendar"
                  readonly
                  dense
                  outlined
                  hide-details
                  clearable
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="filters.lastVisitBefore = null"
                />
              </template>
              <v-date-picker
                v-model="filters.lastVisitBefore"
                no-title
                locale="ja"
                :day-format="date => new Date(date).getDate()"
                @input="endDateMenu = false"
              />
            </v-menu>
          </v-col>
        </v-row>
      </div>

      <!-- 誕生日フィルター（月選択） -->
      <div class="mb-4">
        <v-select
          v-model="filters.birthdayMonth"
          :items="birthdayMonthOptions"
          item-text="text"
          item-value="value"
          label="誕生日"
          dense
          outlined
          hide-details
          clearable
        />
      </div>

      <!-- 関連キャストフィルター -->
      <div class="mb-4">
        <v-select
          v-model="filters.castIds"
          :items="castUsers"
          item-text="name"
          item-value="id"
          label="関連キャスト"
          multiple
          chips
          small-chips
          deletable-chips
          dense
          outlined
          hide-details
          clearable
        />
      </div>

      <!-- 指名受領キャストフィルター -->
      <div class="mb-4">
        <v-select
          v-model="filters.nominationRecipientIds"
          :items="castUsers"
          item-text="name"
          item-value="id"
          label="指名受領キャスト"
          multiple
          chips
          small-chips
          deletable-chips
          dense
          outlined
          hide-details
          clearable
        />
      </div>

      <!-- 顧客タグフィルター -->
      <div class="mb-4">
        <v-select
          v-model="filters.customerTagIds"
          :items="customerTags"
          item-text="name"
          item-value="id"
          label="顧客タグ"
          multiple
          chips
          small-chips
          deletable-chips
          dense
          outlined
          hide-details
          clearable
        />
      </div>
    </div>

    <div class="d-flex justify-end pa-3">
      <v-btn
        text
        @click="handleReset"
      >
        リセット
      </v-btn>
      <v-btn
        color="primary"
        @click="handleApply"
      >
        <v-icon left>
          {{ icons.mdiMagnify }}
        </v-icon>
        適用
      </v-btn>
    </div>
  </div>
</template>

<script>
import {
  reactive, ref, onMounted,
} from '@vue/composition-api'
import {
  mdiFilterVariant, mdiCalendar, mdiMagnify,
} from '@mdi/js'
import { SEGMENT_LABELS, SEGMENT_DESCRIPTIONS, SEGMENT_COLORS } from '../composables/useRfmAnalysis'
import UserApi from '@/api/waiter/User'

const getDefaultFilters = () => ({
  rfmSegment: [],
  minVisitCount: null,
  visitCountStartDate: null,
  visitCountEndDate: null,
  lastVisitAfter: null,
  lastVisitBefore: null,
  customerTagIds: [],
  castIds: [],
  nominationRecipientIds: [],
  birthdayMonth: null,
})

export default {
  name: 'CustomerFilterPanel',
  props: {
    customerTags: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const filters = reactive(getDefaultFilters())
    const startDateMenu = ref(false)
    const endDateMenu = ref(false)
    const visitCountStartDateMenu = ref(false)
    const visitCountEndDateMenu = ref(false)

    const castUsers = ref([])

    const fetchCastUsers = async () => {
      try {
        const res = await UserApi.getUsers('cast')
        if (res.data?.users?.data) {
          castUsers.value = res.data.users.data.map(u => ({
            id: Number(u.id),
            name: u.attributes.name,
          }))
        }
      } catch (e) {
        console.error('Failed to fetch cast users:', e)
      }
    }

    onMounted(() => {
      fetchCastUsers()
    })

    const birthdayMonthOptions = Array.from({ length: 12 }, (_, i) => ({
      text: `${i + 1}月`,
      value: i + 1,
    }))

    const handleApply = () => {
      emit('apply', {
        rfmSegment: filters.rfmSegment,
        minVisitCount: filters.minVisitCount,
        visitCountStartDate: filters.visitCountStartDate,
        visitCountEndDate: filters.visitCountEndDate,
        lastVisitAfter: filters.lastVisitAfter,
        lastVisitBefore: filters.lastVisitBefore,
        customerTagIds: filters.customerTagIds,
        castIds: filters.castIds,
        nominationRecipientIds: filters.nominationRecipientIds,
        birthdayMonth: filters.birthdayMonth,
      })
    }

    const handleReset = () => {
      const defaults = getDefaultFilters()
      Object.keys(defaults).forEach(key => {
        filters[key] = defaults[key]
      })
      emit('reset')
    }

    return {
      filters,
      startDateMenu,
      endDateMenu,
      visitCountStartDateMenu,
      visitCountEndDateMenu,
      castUsers,
      birthdayMonthOptions,
      handleApply,
      handleReset,
      SEGMENT_LABELS,
      SEGMENT_DESCRIPTIONS,
      SEGMENT_COLORS,
      icons: {
        mdiFilterVariant,
        mdiCalendar,
        mdiMagnify,
      },
    }
  },
}
</script>

<style scoped>
.filter-panel {
  display: flex;
  flex-direction: column;
}
.filter-panel__content {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  padding: 16px;
}
</style>
